import { cn } from '../../utils/cn'
import { useRef, useState, ReactNode, HTMLAttributes } from 'react'

interface SpotlightCardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  className?: string
  spotlightColor?: string
}

export function SpotlightCard({
  children,
  className,
  spotlightColor = 'rgba(59, 130, 246, 0.1)',
  onClick,
  ...props
}: SpotlightCardProps) {
  const divRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [opacity, setOpacity] = useState(0)

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return
    const rect = divRef.current.getBoundingClientRect()
    setPosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })
  }

  const handleMouseEnter = () => setOpacity(1)
  const handleMouseLeave = () => setOpacity(0)

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      className={cn(
        'relative overflow-hidden rounded-xl',
        'transition-all duration-200 cursor-pointer',

        // Light theme - clean modern look
        'bg-white border-slate-200/80',
        'hover:bg-slate-50/80 hover:border-slate-300/60',
        'hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]',

        // Dark theme - glass effect
        'dark:bg-slate-900/50 dark:border-slate-700/50',
        'dark:hover:bg-slate-800/60 dark:hover:border-slate-600/50',

        'border backdrop-blur-sm',
        className
      )}
      {...props}
    >
      {/* Spotlight effect */}
      <div
        className="pointer-events-none absolute -inset-px transition-opacity duration-300"
        style={{
          opacity,
          background: `radial-gradient(400px circle at ${position.x}px ${position.y}px, ${spotlightColor}, transparent 40%)`,
        }}
      />
      {children}
    </div>
  )
}
